import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { userSelectors } from 'reducers';
import { getRenderRoleName, getUserIdFromIri, parseDate, parseFullName } from 'utils/helpers';
import { RoleNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionsMenu, OnlineBadge, StyledTableRow } from 'components';
import {
    MenuChangeRole,
    MenuDeactivate,
    MenuDeleteUser,
    MenuSendMessage,
    MenuSubstitute,
} from 'components/ActionsMenu/actions';
import { TableCell } from '@mui/material';
import PropTypes from 'prop-types';

const DeleteUserItem = React.forwardRef((props, ref) => <MenuDeleteUser ref={ref} {...props} />);
const ImpersonateItem = React.forwardRef((props, ref) => <MenuSubstitute ref={ref} {...props} />);
const SendMessageItem = React.forwardRef((props, ref) => <MenuSendMessage ref={ref} {...props} />);
const DeactivateItem = React.forwardRef((props, ref) => <MenuDeactivate ref={ref} {...props} />);
const ChangeRoleItem = React.forwardRef((props, ref) => <MenuChangeRole ref={ref} {...props} />);

const PromoterUserRow = ({ user: { availabilityStatus: status, ...user }, userIsPromoter }) => {
    const internalUser = useSelector(userSelectors.getInternalUser);

    const roleName = useMemo(() => getRenderRoleName(user.subscriptions?.[0]?.role) || '-', [user]);
    const canChangeRole = useMemo(
        () =>
            [
                RoleNames.SERVICER_ADMIN,
                RoleNames.SERVICER_SIGNATORY,
                RoleNames.PROMOTER_STANDARD,
            ].includes(internalUser?.subscriptions?.[0]?.role?.name) &&
            user.id !== internalUser?.id,
        [internalUser, user.id]
    );

    const impersonatedByUserId = useMemo(
        () =>
            internalUser?.impersonatedByUser
                ? getUserIdFromIri(internalUser.impersonatedByUser)
                : null,
        [internalUser]
    );

    return (
        <StyledTableRow key={user.id}>
            <TableCell>
                <OnlineBadge online={user.online} />
            </TableCell>
            <TableCell>{parseFullName(user)}</TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell>{user.phoneNumber || '-'}</TableCell>
            <TableCell>{roleName}</TableCell>
            {!userIsPromoter && <TableCell>{user.promoter?.name}</TableCell>}
            <TableCell>{parseDate(user?.lastLoggedAt) || '-'}</TableCell>
            <TableCell>
                <ActionsMenu autoClose>
                    <DeleteUserItem id={user.id} />
                    <ImpersonateItem
                        user={user}
                        originalPath={'/provider-users'}
                        itemText={texts.SUBSTITUER}
                    />
                    <SendMessageItem receiver={user} />
                    <DeactivateItem
                        id={user.id}
                        impersonatedByUserId={impersonatedByUserId}
                        currentUserId={internalUser.id}
                        status={status}
                    />
                    {canChangeRole && (
                        <ChangeRoleItem
                            user={user}
                            userSubscription={user.subscriptions[0]}
                            isPromoter={true}
                        />
                    )}
                </ActionsMenu>
            </TableCell>
        </StyledTableRow>
    );
};

PromoterUserRow.propTypes = {
    user: PropTypes.object,
    userIsPromoter: PropTypes.bool,
};
export default PromoterUserRow;
